import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, DAG_RUNS_ORDER_BY, RUN_LIMITS } from '../../constants';
import { AirflowDagRun } from '../../types';
import { toNumber } from '../../utils';

const DEFAULT_PAGE_SIZE = RUN_LIMITS[1];

type AdvancedFilters = {
    logicalDateFrom: string;
    logicalDateTo: string;
    runAfterFrom: string;
    runAfterTo: string;
    startDateFrom: string;
    startDateTo: string;
    endDateFrom: string;
    endDateTo: string;
    durationFrom: string;
    durationTo: string;
    runIdPattern: string;
    triggeringUser: string;
};

const EMPTY_ADVANCED_FILTERS: AdvancedFilters = {
    logicalDateFrom: '',
    logicalDateTo: '',
    runAfterFrom: '',
    runAfterTo: '',
    startDateFrom: '',
    startDateTo: '',
    endDateFrom: '',
    endDateTo: '',
    durationFrom: '',
    durationTo: '',
    runIdPattern: '',
    triggeringUser: '',
};

type ConfirmAction = { type: 'success' | 'failed' | 'delete'; run: AirflowDagRun };

/*
 * Manages the DAG Runs tab: sort/page-size, quick state/run-type filters, the advanced
 * filter popover (staged draft -> apply), server-side pagination, and row actions
 */
export function useRunsTab(dagId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();

    const [runs, setRuns] = useState<AirflowDagRun[]>([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [sortBy, setSortBy] = useState(DAG_RUNS_ORDER_BY);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [page, setPage] = useState(1);
    const [stateFilter, setStateFilter] = useState('');
    const [runTypeFilter, setRunTypeFilter] = useState('');
    const [appliedFilters, setAppliedFilters] = useState<AdvancedFilters>(EMPTY_ADVANCED_FILTERS);
    const [draftFilters, setDraftFilters] = useState<AdvancedFilters>(EMPTY_ADVANCED_FILTERS);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

    /*
     * Fetches runs against the current sort/pagination/filter state
     */
    const fetchRuns = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await AirflowDashboardHandler.listDagRuns(Env, dagId, {
                limit: pageSize,
                offset: (page - 1) * pageSize,
                order_by: sortBy,
                state: stateFilter ? [stateFilter] : undefined,
                runType: runTypeFilter ? [runTypeFilter] : undefined,
                logicalDateGte: appliedFilters.logicalDateFrom ? new Date(appliedFilters.logicalDateFrom).toISOString() : undefined,
                logicalDateLte: appliedFilters.logicalDateTo ? new Date(appliedFilters.logicalDateTo).toISOString() : undefined,
                runAfterGte: appliedFilters.runAfterFrom ? new Date(appliedFilters.runAfterFrom).toISOString() : undefined,
                runAfterLte: appliedFilters.runAfterTo ? new Date(appliedFilters.runAfterTo).toISOString() : undefined,
                startDateGte: appliedFilters.startDateFrom ? new Date(appliedFilters.startDateFrom).toISOString() : undefined,
                startDateLte: appliedFilters.startDateTo ? new Date(appliedFilters.startDateTo).toISOString() : undefined,
                endDateGte: appliedFilters.endDateFrom ? new Date(appliedFilters.endDateFrom).toISOString() : undefined,
                endDateLte: appliedFilters.endDateTo ? new Date(appliedFilters.endDateTo).toISOString() : undefined,
                durationGte: toNumber(appliedFilters.durationFrom),
                durationLte: toNumber(appliedFilters.durationTo),
                runIdPattern: appliedFilters.runIdPattern || undefined,
                triggeringUserNamePattern: appliedFilters.triggeringUser || undefined,
            });
            setRuns(response.dag_runs);
            setTotalEntries(response.total_entries ?? response.dag_runs.length);
        } catch {
            errorAlert('Failed to load DAG runs.');
        } finally {
            setIsLoading(false);
        }
    }, [Env, dagId, pageSize, page, sortBy, stateFilter, runTypeFilter, appliedFilters]);

    useEffect(() => {
        fetchRuns();
    }, [fetchRuns, refreshKey]);

    /*
     * Sets the state filter and resets to the first page
     */
    const handleStateFilterChange = useCallback((value: string) => {
        setStateFilter(value);
        setPage(1);
    }, []);

    /*
     * Sets the run-type filter and resets to the first page
     */
    const handleRunTypeFilterChange = useCallback((value: string) => {
        setRunTypeFilter(value);
        setPage(1);
    }, []);

    /*
     * Copies applied filters into the draft state when the advanced popover opens
     */
    const handleOpenAdvancedFilters = useCallback(() => {
        setDraftFilters(appliedFilters);
    }, [appliedFilters]);

    /*
     * Updates a single draft filter field
     */
    const handleDraftFilterChange = useCallback((key: keyof AdvancedFilters, value: string) => {
        setDraftFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    /*
     * Commits the draft filters and resets to the first page
     */
    const handleApplyAdvancedFilters = useCallback(() => {
        setAppliedFilters(draftFilters);
        setPage(1);
    }, [draftFilters]);

    /*
     * Clears a single applied filter chip
     */
    const handleClearFilter = useCallback((key: keyof AdvancedFilters) => {
        setAppliedFilters((prev) => ({ ...prev, [key]: '' }));
        setPage(1);
    }, []);

    /*
     * Clears every advanced filter, the state filter, and the run-type filter
     */
    const handleClearAllFilters = useCallback(() => {
        setAppliedFilters(EMPTY_ADVANCED_FILTERS);
        setDraftFilters(EMPTY_ADVANCED_FILTERS);
        setStateFilter('');
        setRunTypeFilter('');
        setPage(1);
    }, []);

    /*
     * Executes the confirmed mark-success/mark-failed/delete action
     */
    const handleConfirmAction = useCallback(async () => {
        if (!confirmAction) return;
        const { type, run } = confirmAction;
        try {
            if (type === 'delete') {
                await AirflowDashboardHandler.deleteDagRun(Env, dagId, run.dag_run_id);
                successAlert(`Deleted run "${run.dag_run_id}".`);
            } else {
                await AirflowDashboardHandler.patchDagRun(Env, dagId, run.dag_run_id, { state: type, note: null });
                successAlert(`Marked run "${run.dag_run_id}" as ${type}.`);
            }
            setConfirmAction(null);
            fetchRuns();
        } catch {
            errorAlert(`Failed to ${type} run "${run.dag_run_id}".`);
        }
    }, [Env, dagId, confirmAction, fetchRuns]);

    /*
     * Navigates to a run's detail page
     */
    const handleRunClick = useCallback(
        (run: AirflowDagRun) => {
            history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}/runs/${encodeURIComponent(run.dag_run_id)}`);
        },
        [history, dagId]
    );

    const totalPages = useMemo(() => Math.max(1, Math.ceil(totalEntries / pageSize)), [totalEntries, pageSize]);
    const activeFilterCount = useMemo(() => Object.values(appliedFilters).filter(Boolean).length, [appliedFilters]);

    return {
        runs,
        totalEntries,
        totalPages,
        isLoading,
        sortBy,
        setSortBy,
        pageSize,
        setPageSize,
        page,
        setPage,
        stateFilter,
        runTypeFilter,
        appliedFilters,
        draftFilters,
        activeFilterCount,
        confirmAction,
        setConfirmAction,
        handleStateFilterChange,
        handleRunTypeFilterChange,
        handleOpenAdvancedFilters,
        handleDraftFilterChange,
        handleApplyAdvancedFilters,
        handleClearFilter,
        handleClearAllFilters,
        handleConfirmAction,
        handleRunClick,
    };
}
